import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Download, Trash2, ShieldOff, FileText, ChevronRight, Sparkles } from "lucide-react";
import ReferralCard from "@/components/ReferralCard";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user?.id) return null;
  return <AccountSettings key={user.id} {...{ user, logout, navigate }} />;
}

function AccountSettings({ user, logout, navigate }) {
  const [blocks, setBlocks] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(false);
  const blockVersion = useRef(0);
  const pendingUnblocks = useRef(new Set());
  const [unblocking, setUnblocking] = useState([]);
  const [blocksState, setBlocksState] = useState("loading");

  const loadBlocks = useCallback(async () => {
    const version = ++blockVersion.current;
    const current = () => mounted.current && version === blockVersion.current;
    setBlocksState("loading");
    try {
      const r = await api.get("/users/me/blocks", { timeout: 30000 });
      if (!current()) return;
      setBlocks(r.data);
      setBlocksState("ready");
    } catch {
      if (current()) setBlocksState("error");
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    loadBlocks();
    return () => { mounted.current = false; blockVersion.current += 1; };
  }, [loadBlocks]);

  const unblock = async (uid) => {
    if (pendingUnblocks.current.has(uid)) return;
    pendingUnblocks.current.add(uid);
    setUnblocking([...pendingUnblocks.current]);
    try {
      await api.post(`/users/${uid}/block`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      toast.success("Unblocked");
      await loadBlocks();
    } catch {
      if (mounted.current) toast.error("Could not unblock");
    } finally {
      pendingUnblocks.current.delete(uid);
      if (mounted.current) setUnblocking([...pendingUnblocks.current]);
    }
  };

  const exportData = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const r = await api.get("/users/me/export", { timeout: 30000 });
      if (!mounted.current) return;
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `haeven-export-${user?.username || "me"}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data is downloading");
    } catch { if (mounted.current) toast.error("Export failed"); }
    finally { busyRef.current = false; if (mounted.current) setBusy(false); }
  };

  const deleteAccount = async () => {
    if (busyRef.current) return;
    if (confirmText.trim() !== "DELETE") return toast.error('Type "DELETE" to confirm');
    if (!currentPassword) return toast.error("Enter your current password");
    busyRef.current = true;
    setBusy(true);
    try {
      await api.delete("/users/me", { data: { current_password: currentPassword }, timeout: 30000 });
      if (!mounted.current) return;
      toast.success("Account deleted. We're sorry to see you go.");
      logout();
      navigate("/");
    } catch (err) { if (mounted.current) toast.error(err?.response?.data?.detail || "Could not delete account"); }
    finally { busyRef.current = false; if (mounted.current) setBusy(false); }
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-4xl">Settings</h1>
        <p className="text-muted-foreground mt-1">Privacy, safety and account controls.</p>
      </div>

      {/* Account quick info */}
      <section className="brutal-card bg-card p-5 flex items-center gap-4" data-testid="settings-account-row">
        <div className="w-12 h-12 rounded-full border-2 border-foreground overflow-hidden bg-sage flex items-center justify-center font-bold">
          {user?.avatar_path ? <img src={fileUrl(user.avatar_path)} className="w-full h-full object-cover" alt="" /> : (user?.display_name?.[0] || "?")}
        </div>
        <div className="flex-1">
          <div className="font-semibold">{user?.display_name}</div>
          <div className="text-xs text-muted-foreground">@{user?.username} · {user?.email}</div>
        </div>
        {user?.premium?.active && (
          <span className="brutal-card bg-accent px-2 py-0.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
            <Sparkles size={12} strokeWidth={3} /> Premium
          </span>
        )}
      </section>

      {/* Premium upgrade prompt (works on web via Stripe, on native via StoreKit/Play Billing) */}
      {!user?.premium?.active && (
        <Link to="/premium" data-testid="settings-upgrade" className="brutal-card bg-accent p-5 flex items-center justify-between hover:translate-x-[-2px] transition-transform">
          <div>
            <div className="font-semibold flex items-center gap-2"><Sparkles size={16} strokeWidth={2.5} /> Upgrade to HAEVEN Premium</div>
            <div className="text-xs mt-1">Ad-free, private journal, weekly AI mood reflection. From $9.99/mo.</div>
          </div>
          <ChevronRight size={18} />
        </Link>
      )}

      {/* Referrals — invite a friend, both get a free month of Premium */}
      <ReferralCard />

      {/* Blocked users */}
      <section className="brutal-card bg-card p-5" data-testid="settings-blocks">
        <div className="flex items-center gap-2 mb-3">
          <ShieldOff size={18} strokeWidth={2.5} />
          <h2 className="text-xl">Blocked accounts</h2>
        </div>
        {blocksState === "loading" ? <p>Loading blocked accounts…</p> : blocksState === "error" ? (
          <div role="alert">Could not load blocked accounts. <button onClick={loadBlocks} className="brutal-btn px-3 py-1">Retry</button></div>
        ) : blocks.length === 0 ? (
          <p className="text-sm text-muted-foreground">You haven&apos;t blocked anyone.</p>
        ) : (
          <ul className="space-y-2">
            {blocks.map((b) => (
              <li key={b.id} className="flex items-center gap-3 brutal-card bg-background p-2" data-testid={`blocked-${b.id}`}>
                <div className="w-8 h-8 rounded-full border-2 border-foreground overflow-hidden bg-sage flex items-center justify-center text-sm font-bold">
                  {b.avatar_path ? <img src={fileUrl(b.avatar_path)} className="w-full h-full object-cover" alt="" /> : b.display_name?.[0]}
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-semibold leading-tight">{b.display_name}</div>
                  <div className="text-xs text-muted-foreground">@{b.username}</div>
                </div>
                <button onClick={() => unblock(b.id)} disabled={unblocking.includes(b.id)} data-testid={`unblock-${b.id}`} className="brutal-btn px-3 py-1 text-xs bg-card disabled:opacity-50">Unblock</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Data export */}
      <section className="brutal-card bg-card p-5" data-testid="settings-export">
        <div className="flex items-center gap-2 mb-2">
          <Download size={18} strokeWidth={2.5} />
          <h2 className="text-xl">Download your data</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">Get a JSON copy of your profile, social content, messages, creator activity, community participation, recommendation controls, AI activity metadata, referrals, device registrations and payment records.</p>
        <button onClick={exportData} disabled={busy} className="brutal-btn px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50" data-testid="export-btn">
          {busy ? "Preparing…" : "Export my data"}
        </button>
      </section>

      {/* Legal */}
      <section className="brutal-card bg-card p-5" data-testid="settings-legal">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={18} strokeWidth={2.5} />
          <h2 className="text-xl">Legal</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/legal/privacy" data-testid="link-privacy" className="brutal-btn px-3 py-2 bg-card text-sm">Privacy Policy</Link>
          <Link to="/legal/terms" data-testid="link-terms" className="brutal-btn px-3 py-2 bg-card text-sm">Terms of Service</Link>
        </div>
      </section>

      {/* Delete account */}
      <section className="brutal-card bg-card p-5" data-testid="settings-delete">
        <div className="flex items-center gap-2 mb-2 text-destructive">
          <Trash2 size={18} strokeWidth={2.5} />
          <h2 className="text-xl">Delete account</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">Permanently removes your account, social content, messages, creator profile content, community participation, recommendations, AI activity metadata, referrals and device registrations. Limited transaction and safety evidence may be retained without your account identity. This cannot be undone.</p>
        {!confirmOpen ? (
          <button onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="delete-account-btn" className="brutal-btn px-4 py-2 bg-destructive text-destructive-foreground">
            Delete my account
          </button>
        ) : (
          <div className="space-y-3" data-testid="delete-confirm">
            <p className="text-sm">Enter your current password, then type <code className="bg-background px-1 border-2 border-foreground">DELETE</code> to confirm.</p>
            <input
              type="password"
              disabled={busy}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="brutal-input w-full px-3 py-2"
              placeholder="Current password"
              aria-label="Current password"
              data-testid="delete-current-password"
            />
            <input
              value={confirmText}
              disabled={busy}
              onChange={(e) => setConfirmText(e.target.value)}
              className="brutal-input w-full px-3 py-2"
              placeholder="DELETE"
              data-testid="delete-confirm-input"
            />
            <div className="flex gap-2">
              <button disabled={busy} onClick={() => { setConfirmOpen(false); setConfirmText(""); setCurrentPassword(""); }} className="brutal-btn px-4 py-2 bg-card">Cancel</button>
              <button onClick={deleteAccount} disabled={busy || !currentPassword || confirmText.trim() !== "DELETE"} className="brutal-btn px-4 py-2 bg-destructive text-destructive-foreground disabled:opacity-50" data-testid="delete-confirm-btn">
                {busy ? "Deleting…" : "Yes, delete forever"}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
